//EnvironmentStore.ts
import { ref, computed, readonly, shallowReadonly } from 'vue'
import { defineStore } from 'pinia'
import type { AuthorizedUser, User } from '@/types'
import http from '@/services/http.service'
import userService from '@/services/user.service'
import Trans from '@/i18n/translation'
import { useToast } from 'vue-toast-notification'
import { isPaletteId, type PaletteId } from '@/ui/palettes'
import type { TriScaleMode } from '@/lib/colorScale'

export const useEnvironmentStore = defineStore(
  'environment',
  () => {
    const toast = useToast({ position: 'bottom', queue: true, duration: 1000 })
    const title = ref<string>('page-titles.login')
    const contextId = ref(0)
    const locale = ref<string>('no')

    const token = ref<string | null>(null)
    const _user = ref<AuthorizedUser | User | null>()
    const _sessionState = ref<'unknown' | 'valid' | 'invalid'>('unknown')

    const _verifyPromise = ref<Promise<boolean> | null>(null)
    const _lastVerifiedAt = ref(0)

    const paletteId = ref<PaletteId>('redYellowGreen')

    const isAuthenticated = computed(() => !!token.value && _sessionState.value === 'valid')
    const supportedLocales = computed(() => Trans.supportedLocales)
    const currentLocale = computed(() => locale.value)
    const currentFlag = computed(
      () =>
        Trans.supportedLocales.find((supportedLocale) => supportedLocale.code === locale.value)
          ?.flag
    )

    const paletteMode = ref<TriScaleMode>('banded')

    function setContextId(newContextId: number) {
      contextId.value = newContextId
    }

    function setUser(user?: AuthorizedUser | User) {
      if (!user) {
        logout()
        return
      }

      _user.value = user
      _sessionState.value = 'unknown'
    }

    async function initUser() {
      if (!_user.value == null) {
        console.log('Refusing to create user, user object already exists')
        return
      }

      const initUser: User = await userService.init()
      if (initUser) {
        _user.value = initUser
      } else {
        console.error('An error occured when initializing temp user')
      }
    }

    async function confirmUser(token: string, tempUser: User): Promise<boolean> {

      const confirmedUser = await userService.confirm(token, tempUser)
      if (confirmedUser) {
        _user.value = confirmedUser
        setToken(confirmedUser.token)
        await verifyToken(true)
        toast.success('Logget inn som ' + _user.value.username, {
          duration: 2500,
          position: 'bottom'
        })

        return true
      } else {
        console.log('Failed to confirm user: ' + JSON.stringify(tempUser))
      }

      return false
    }

    async function login(username: string, password: string): Promise<boolean> {
      const loggedInUser = await userService.login(username, password).catch(() => {
        return undefined
      })

      if (loggedInUser) {
        _user.value = loggedInUser
        setToken(loggedInUser.token)
        await verifyToken(true)
        toast.success('Logget inn som ' + loggedInUser.username, {
          duration: 2500,
          position: 'bottom'
        })
        return true
      } else {
        toast.warning('Login failed', { duration: 2500, position: 'bottom' })
      }

      return false
    }

    async function setLocale(newLocale: string) {
      if (Trans.isLocaleSupported(newLocale)) {
        try {
          await Trans.switchLanguage(newLocale)
          locale.value = newLocale
        } catch (error) {
          console.error('Error switching locale:', error)
        }
      }
    }

    function logout() {
      _user.value = undefined
      token.value = ''
      _sessionState.value = 'invalid'
    }

    async function guessDefaultLocale() {
      return Trans.guessDefaultLocale()
    }

    function setToken(t: string | null) {
      token.value = t
      _sessionState.value = 'unknown'
      if (!t) _user.value = null
    }

    function hydrateFromStorage() {
      _sessionState.value = token.value ? 'unknown' : 'invalid'
      if (!token.value) _user.value = null
    }

    function markInvalid() {
      _sessionState.value = 'invalid'
      _user.value = null
    }

    function clear() {
      setToken(null)
      markInvalid()
    }

    function setPaletteMode(newMode: TriScaleMode) {
      paletteMode.value = newMode
    }

    async function verifyToken(force = false): Promise<boolean> {
      if (!token.value) {
        markInvalid()
        return false
      }

      const now = Date.now()
      if (!force && _sessionState.value === 'valid' && now - _lastVerifiedAt.value < 60_000) {
        return true
      }

      if (_verifyPromise.value) return _verifyPromise.value

      _verifyPromise.value = (async () => {
        try {
          const me = await http.get<any>('auth/me')
          _user.value = me
          _sessionState.value = 'valid'
          _lastVerifiedAt.value = Date.now()
          return true
       } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) clear()
        else markInvalid()
        return false
      }
      finally {
        _verifyPromise.value = null
      }
      })()

      return _verifyPromise.value
    }

    async function ensureSession(): Promise<boolean> {
      if (_sessionState.value === 'valid') return true
      if (token.value) return verifyToken()
      return false
    }

    function setPaletteId(newPaletteId: PaletteId) {
      if (!isPaletteId(newPaletteId)) return
      paletteId.value = newPaletteId
    }

    return {
      token,
      user: shallowReadonly(_user), // shallow to allow nested mutations if needed
      sessionState: readonly(_sessionState),
      isAuthenticated,
      setToken,
      hydrateFromStorage,
      markInvalid,
      clear,
      verifyToken,
      ensureSession,
      title,
      contextId,
      setContextId,
      setUser,
      logout,
      currentLocale,
      setLocale,
      supportedLocales,
      currentFlag,
      guessDefaultLocale,
      initUser,
      confirmUser,
      login,
      setPaletteId,
      paletteId,
      paletteMode,
      setPaletteMode,
    }
  },
  {
    persist: {
      storage: localStorage,
      pick: ['locale', 'token', 'paletteId', 'paletteMode'],
    }
  }
)

export default useEnvironmentStore
